import { UIPaths } from '@consts';
import { useGoalsService } from '@hooks';
import { ComboIcon, LoaderIcon } from '@icons';
import { GoalCardLinkTarget, GoalModel } from '@models';
import { setupForDark } from '@theme';
import { useIntersectionObserver } from '@uidotdev/usehooks';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import GoalCard from './GoalCard';

const ComboGoalsHorizontalGoalsList = ({
  childGoalEntityId,
  linkTarget = GoalCardLinkTarget.GOAL
}: {
  childGoalEntityId: string;
  linkTarget?: GoalCardLinkTarget;
}) => {
  const { getGoals } = useGoalsService();
  const [goals, setGoals] = useState<GoalModel[]>([]);
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [wasVisible, setWasVisible] = useState(false);
  const [ref, entry] = useIntersectionObserver({
    root: null,
    rootMargin: '0px',
    threshold: 0.1
  });

  useEffect(() => {
    if (wasVisible) {
      setIsLoading(true);
      getGoals({ childGoalEntityId: childGoalEntityId })
        .then(({ goals }) => setGoals(goals))
        .finally(() => setIsLoading(false));
    } else {
      setGoals([]);
    }
  }, [childGoalEntityId, wasVisible]);

  useEffect(() => {
    if (entry?.isIntersecting) {
      setWasVisible(true);
    }
  }, [entry?.isIntersecting]);

  if (!wasVisible) {
    return <div ref={ref} />;
  }

  if (!isLoading && goals.length === 0) {
    return <div ref={ref} />;
  }

  return (
    <div
      ref={ref}
      className='flex flex-col gap-sm w-full'
      data-testid={`comboGoalsHorizontalGoalsList-goal-${childGoalEntityId}`}
    >
      {!isLoading && goals.length > 1 && (
        <div
          className={classNames(
            'LS2B flex flex-row gap-sm justify-start items-center pl-sm',
            setupForDark('text-secondary')
          )}
        >
          <ComboIcon />
          <div className={classNames('uppercase')}>
            {t('domains.goals.comboGoalsHorizontalListHeader')}
          </div>
        </div>
      )}
      {isLoading && (
        <div className='flex flex-col gap-sm w-full justify-center items-center'>
          <LoaderIcon size={24} />
        </div>
      )}
      <div className='flex flex-row gap-sm w-full overflow-x-auto'>
        {goals.map((goal) => (
          <GoalCard
            key={goal.entityId}
            goal={goal}
            showActions={{
              like: true,
              share: true
            }}
            hideChevron={true}
            className={classNames(
              { 'min-h-full w-full': goals.length === 1 },
              { 'min-h-full w-[350px] sm:w-[600px]': goals.length > 1 }
            )}
            goalSubscription={goal.subscription}
            customLink={
              linkTarget === GoalCardLinkTarget.SUBSCRIPTION && goal.subscription
                ? UIPaths.goalSubscription(goal?.subscription?.id)
                : UIPaths.goal(goal.entityId)
            }
          />
        ))}
      </div>
    </div>
  );
};

export default ComboGoalsHorizontalGoalsList;
